import { Logger, LogLevel } from '@amplitude/analytics-types'

const PREFIX = 'Amplitude Logger '

/**
 * Drop-in for the SDK's default logger with one difference: when a plugin filters an event out by
 * returning null, the SDK resolves the event with code 0 and an empty message, and the client routes
 * every code other than 200 and 100 to error(). That prints "Amplitude Logger [Error]: " with nothing
 * behind it, on every intentionally dropped event (e.g. gallery interactions after the first).
 * An error with no message carries no information, so it is skipped here instead of hitting
 * console.error, where Sentry breadcrumbs, the Next dev overlay and React DevTools all pick it up.
 */
export class AmplitudeLogger implements Logger {
  private logLevel: LogLevel = LogLevel.None

  debug(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Debug) {
      return
    }

    // console.debug output is hidden by default in chrome
    console.log(`${PREFIX}[Debug]: ${args.join(' ')}`)
  }

  disable(): void {
    this.logLevel = LogLevel.None
  }

  enable(logLevel: LogLevel = LogLevel.Warn): void {
    this.logLevel = logLevel
  }

  error(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Error) {
      return
    }

    const message = args.join(' ')
    if (message === '') {
      return
    }

    console.error(`${PREFIX}[Error]: ${message}`)
  }

  log(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Verbose) {
      return
    }

    console.log(`${PREFIX}[Log]: ${args.join(' ')}`)
  }

  warn(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Warn) {
      return
    }

    console.warn(`${PREFIX}[Warn]: ${args.join(' ')}`)
  }
}
